import type { Claim } from '../types';
import { billedOf, codesOf, formatMoney } from '../utils';
import { AUTOPILOT_TODAY } from '../fixtures';
import { Link } from 'react-router-dom';
import { isPlainClick } from '@/components/plainClick';

export function AutopilotSummary({
  waiting,
  onReview,
}: {
  waiting: Claim[];
  onReview: (id: string) => void;
}) {
  const rows = [...waiting].sort((a, b) => (a.serviceDate < b.serviceDate ? -1 : a.serviceDate > b.serviceDate ? 1 : 0));
  const waitingTotal = rows.reduce((s, c) => s + billedOf(c), 0);

  return (
    <div className="clm-ap-page">
      {/* Today card */}
      <section className="clm-card clm-ap-today">
        <div>
          <div className="clm-ap-stat-label">Submitted by ELVA today</div>
          <div className="clm-ap-stat-value num">
            {AUTOPILOT_TODAY.claims} claims · {formatMoney(AUTOPILOT_TODAY.sum)}
          </div>
        </div>
        <div>
          <div className="clm-ap-stat-label">Waiting for you</div>
          <div className="clm-ap-stat-value num">
            {rows.length} · {formatMoney(waitingTotal)}
          </div>
        </div>
        <Link to="/claims/all?status=sent" className="clm-src clm-ap-link" style={{ textDecoration: 'underline' }}>
          See today’s submissions in Every claim
        </Link>
      </section>

      {/* Why these came back to you */}
      <section className="clm-card" aria-labelledby="clm-why">
        {rows.length === 0 ? (
          <p>
            Nothing needs you. ELVA submitted {AUTOPILOT_TODAY.claims} claims today.{' '}
            <Link to="/claims/all?status=sent" className="clm-src" style={{ textDecoration: 'underline' }}>
              See today’s submissions in Every claim
            </Link>
          </p>
        ) : (
          <>
            <div className="clm-card-head">
              <h2 className="clm-h2" id="clm-why">
                Why these came back to you
              </h2>
              <span className="clm-meta">ELVA held each one for a reason it can name. Confirm them one at a time.</span>
            </div>
            <div className="clm-tbl-wrap">
              <table className="clm-tbl">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Why ELVA held it</th>
                    <th className="ta-right">Billed</th>
                    <th className="clm-visually-hidden">Review</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const codes = codesOf(c.procedures);
                    const codesText =
                      codes.length === 1 ? `${codes[0] ?? ''} ${c.procedureGroup}` : codes.join(', ');
                    return (
                      <tr key={c.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{c.patient}</div>
                          <div className="clm-meta">
                            {c.payer.short} · {codesText}
                          </div>
                        </td>
                        <td style={{ color: 'var(--fg2)' }}>{c.heldReason}</td>
                        <td className="ta-right num" style={{ fontWeight: 600 }}>
                          {formatMoney(billedOf(c))}
                        </td>
                        <td className="ta-right">
                          <a
                            href={`/claims/${c.id}`}
                            className="clm-btn clm-btn-sm"
                            onClick={(e) => {
                              if (!isPlainClick(e)) return;
                              e.preventDefault();
                              onReview(c.id);
                            }}
                          >
                            Review
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
